import { useMemo } from 'react';
import { getPrimePendingMonthYear } from '@bloomays-lib/utils.shared';
import type { CLevelSynthesisRow, LifetimeMarginCumulAggregate } from '../types/primes';
import { isCurrentYear } from '../utils/annualPrime.utils';
import {
  buildAnnualMarginAmountByTam,
  buildAnnualMarginAmountByTamUpToMonth,
  buildAnnualPrimeAmountByTam,
  sumLifetimeMarginCumuls,
  sumSuccessfulRowAmounts,
} from '../utils/lifetimeMargin.utils';

export type CLevelSynthesisKpis = {
  marginGenerated: { amount: number; evolutionPercent: number | null; progressPercent: number };
  primesCalculated: { amount: number; contributingTamCount: number };
  lifetimeMarginTotal: { amount: number; activeTamCount: number };
  synthesisLoading: boolean;
  marginEvolutionLoading: boolean;
};

/** Coupure mensuelle (MM/yyyy) de la période "réalisée" de l'année sélectionnée : mois en cours si année courante, décembre sinon (année passée déjà entièrement validée). */
const buildCutoffMonthYear = (selectedYear: string): string =>
  isCurrentYear(selectedYear) ? getPrimePendingMonthYear() : `12/${selectedYear}`;

export const useCLevelSynthesisKpis = (
  synthesisRows: CLevelSynthesisRow[],
  selectedYear: string,
  previousYearRows: CLevelSynthesisRow[],
  cumuls: LifetimeMarginCumulAggregate[],
): CLevelSynthesisKpis =>
  useMemo(() => {
    const previousYear = String(Number(selectedYear) - 1);
    const marginByTam = buildAnnualMarginAmountByTam(synthesisRows);
    const primeByTam = buildAnnualPrimeAmountByTam(synthesisRows);
    const marginGeneratedAmount = sumSuccessfulRowAmounts(synthesisRows, marginByTam);
    const primesCalculatedAmount = sumSuccessfulRowAmounts(synthesisRows, primeByTam);
    const contributingTamCount = synthesisRows.filter(
      (row) => row.rowStatus === 'success' && (primeByTam[row.tamEmail] ?? 0) > 0,
    ).length;

    const cutoffMonthYear = buildCutoffMonthYear(selectedYear);
    const cutoffMonth = Number(cutoffMonthYear.split('/')[0]);
    const previousPeriodCutoff = `${cutoffMonthYear.split('/')[0]}/${previousYear}`;
    const previousMarginByTam = buildAnnualMarginAmountByTamUpToMonth(previousYearRows, previousPeriodCutoff);
    const previousMarginAmount = sumSuccessfulRowAmounts(previousYearRows, previousMarginByTam);
    const lifetimeMarginTotalAmount = sumLifetimeMarginCumuls(cumuls);
    const synthesisLoading = synthesisRows.some((row) => row.rowStatus === 'loading');
    const marginEvolutionLoading = previousYearRows.some((row) => row.rowStatus === 'loading');
    const evolutionPercent =
      synthesisLoading || marginEvolutionLoading
        ? null
        : previousMarginAmount > 0
        ? Math.round(((marginGeneratedAmount - previousMarginAmount) / previousMarginAmount) * 100)
        : null;

    return {
      marginGenerated: {
        amount: marginGeneratedAmount,
        evolutionPercent,
        progressPercent: Math.round((cutoffMonth / 12) * 100),
      },
      primesCalculated: {
        amount: primesCalculatedAmount,
        contributingTamCount,
      },
      lifetimeMarginTotal: {
        amount: lifetimeMarginTotalAmount,
        activeTamCount: cumuls.length,
      },
      synthesisLoading,
      marginEvolutionLoading,
    };
  }, [cumuls, previousYearRows, selectedYear, synthesisRows]);
